import { useEffect, useMemo, useState } from "react";

import { NotebookQuickViewUI } from "@/components/notebook/NotebookQuickView.ui";
import { NotebookError, useNotebook } from "@/hooks/useNotebook";
import { notebookReadFailed } from "@domain/user-errors";

interface NotebookQuickViewContainerProps {
  /** Promote to the full-page workspace at /notebook; a `path` deep-links that
   *  exact note (`/notebook?path=…`). */
  onOpenPage: (path?: string) => void;
}

/**
 * Data half of the Notebook prop window. Because it is only mounted inside the
 * chat FloatingWindow when the notebook prop is active, `useNotebook`'s
 * fetch-on-mount fires only when the window is opened — never eagerly on the
 * chat route (spec §4.3). Search is title/path-only over the metadata-only list.
 *
 * Opening a row previews the note IN PLACE rather than navigating away, so
 * reading a note no longer costs you the 3D world you were standing in
 * (`StudioWorldWindow.container` precedent — a world window may host a
 * read-only detail view; anything beyond reading still hands off to the page).
 * The body is read lazily on open: the list is metadata-only by design, and
 * prefetching every row's content would be N GETs for one the user wanted.
 */
export function NotebookQuickViewContainer({ onOpenPage }: NotebookQuickViewContainerProps) {
  // ONE `useNotebook` instance for this surface — a second call would
  // double-fetch and fork the snapshot (§4.13 single-hook rule).
  const { notes, isLoading, lastRefreshedAt, refresh, readNote } = useNotebook();
  const [query, setQuery] = useState("");
  /** Open note as a PATH, never a `.find()`-derived object — identity IS the
   *  path (§4.13), and a derived object is the §4.3 render-gate antipattern. */
  const [openPath, setOpenPath] = useState<string | null>(null);
  const [openContent, setOpenContent] = useState<string | null>(null);
  const [isReading, setIsReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) => n.path.toLowerCase().includes(q));
  }, [notes, query]);

  useEffect(() => {
    if (openPath === null) return;
    let cancelled = false;
    setIsReading(true);
    setReadError(null);
    setNotFound(false);
    setOpenContent(null);
    readNote({ path: openPath })
      .then((n) => {
        if (!cancelled) setOpenContent(n.content);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Branch on the CODE, never display copy: only a true NOT_FOUND means
        // the note is gone. A transport failure must not claim it was deleted.
        if (err instanceof NotebookError && err.code === "NOT_FOUND") {
          setNotFound(true);
        } else {
          setReadError(
            err instanceof NotebookError ? err.userMessage : notebookReadFailed(),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsReading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [openPath, readNote]);

  return (
    <NotebookQuickViewUI
      notes={filtered}
      query={query}
      isLoading={isLoading}
      lastRefreshedAt={lastRefreshedAt}
      onQueryChange={setQuery}
      onRefresh={() => void refresh()}
      onOpenPage={onOpenPage}
      openPath={openPath}
      openContent={openContent}
      isReading={isReading}
      readError={readError}
      notFound={notFound}
      onOpenNote={setOpenPath}
      onCloseNote={() => setOpenPath(null)}
    />
  );
}
